import {dataTestId} from '@hconnect/uikit'
import {Divider, ListItemButton, ListItemText, Box} from '@mui/material'
import React, {useEffect, useState} from 'react'
import {NodeRendererProps} from 'react-arborist'
import {useNavigate, useSearchParams} from 'react-router'

import {MaterialTreeNode, isExistingMaterialGuard, isNewMaterialGuard} from '../../types'

import {MaterialTypeNodeActions} from './MaterialCategoryNodeActions'
import {MaterialNodeActions} from './MaterialNodeActions'
import {FilterOptions} from './MaterialsTreeFilters'
import {NewMaterialNodeActions} from './NewMaterialNodeActions'

import {PLANT_ROUTES, getUrl, useUrlParam, useTryGetUrlParam} from '@settings/modules/routing'

export const MaterialsTreeNode: React.FC<NodeRendererProps<MaterialTreeNode>> = ({node, style}) => {
  const plantCode = useUrlParam('plantCode')
  const materialType = useTryGetUrlParam('materialType')
  const materialId = useTryGetUrlParam('materialId')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [isInitializedNode, setIsInitializedNode] = useState(false)

  const originalEntity = node.data.originalEntity
  const isExistingMaterialNode = isExistingMaterialGuard(originalEntity)
  const isNewMaterialNode = isNewMaterialGuard(originalEntity)
  const isMaterialNode = isExistingMaterialNode || isNewMaterialNode

  const onTreeNodeClick = (e: React.MouseEvent) => {
    if (isExistingMaterialNode) {
      navigate(
        getUrl(PLANT_ROUTES.MATERIALS.TYPE.MATERIAL_TYPE.ID.MATERIAL_ID.path, {
          plantCode,
          materialType: originalEntity.type,
          materialId: String(originalEntity.id)
        })
      )
    } else {
      node.toggle()

      if (!isMaterialNode) {
        const urlParams = new URLSearchParams()

        const filter = searchParams.get('filter') as FilterOptions | null
        const filterText = searchParams.get('filterText')?.toLowerCase() || ''

        if (filter && filter !== FilterOptions.All) {
          urlParams.set('filter', filter)
        }
        if (filterText) {
          urlParams.set('filterText', filterText)
        }

        const baseUrl = getUrl(PLANT_ROUTES.MATERIALS.TYPE.MATERIAL_TYPE.path, {
          plantCode,
          materialType: node.data.id
        })

        navigate(urlParams.toString() ? `${baseUrl}?${urlParams.toString()}` : baseUrl)
      }

      // We use stopPropagation to not remove selection from the leaf node
      e.stopPropagation()

      setIsInitializedNode(true)
    }
  }

  useEffect(() => {
    // If we have a new material node, we need to select it
    if (isNewMaterialNode && !node.isSelected) node.select()
  }, [isNewMaterialNode, node])

  useEffect(() => {
    if (materialType && materialId === undefined) {
      if (!isInitializedNode && node.isClosed && node.data.id === materialType) {
        node.open()
        setIsInitializedNode(true)
      }
    }
  }, [materialType, materialId, node, isInitializedNode])

  return (
    <Box {...dataTestId(`settings-tree-${isMaterialNode ? 'leaf' : 'internal'}`)}>
      <ListItemButton
        selected={isMaterialNode && node.isSelected}
        onClick={onTreeNodeClick}
        style={style}
        sx={{height: '58px'}}
        {...dataTestId(`settings-tree-item-${isMaterialNode ? node.data.name : originalEntity}`)}
      >
        <ListItemText
          primary={node.data.name}
          secondary={isMaterialNode && !isNewMaterialNode && originalEntity.globalName}
          sx={{pl: 1.5}}
          primaryTypographyProps={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'block',
            width: 'calc(100% - 32px)'
          }}
        />
        {!isMaterialNode && <MaterialTypeNodeActions node={node} />}
        {isExistingMaterialNode && node.isSelected && <MaterialNodeActions node={node} />}
        {isNewMaterialNode && <NewMaterialNodeActions node={node} />}
      </ListItemButton>
      <Divider />
    </Box>
  )
}
